import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { ordersApi } from 'services/apiDigitalTicket/orders';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import * as CompaniesActions from 'store/ducks/companies/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import { getBoletoUrl } from 'helpers/boleto';
import CONST_ORDERS from 'constants/orders';
import CONST_PAYMENT from 'constants/payments';
import PATH from 'constants/basePath';

import * as OrdersActions from './actions';
import { OrdersTypes } from './types';

function parseJsonToValidObject(str: string) {
  let obj;
  try {
    obj = JSON.parse(str);
  } catch (e) {
    return str;
  }
  return obj;
}

function* list({
  payload,
}: ReturnType<typeof OrdersActions.listOrdersRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando pedidos' }));
    const isCvFull = payload?.isCvFull;
    delete payload?.isCvFull;

    const response = yield call(
      ordersApi[
        (payload as { isInstituition?: boolean })?.isInstituition
          ? 'list'
          : 'get'
      ],
      {
        params: { ...payload },
      },
    );

    if (response.status === 200) {
      let result = [];
      if (Array.isArray(response.data)) {
        result = response.data;
      } else {
        result = [response.data];
      }

      const data = result.map((order: any) => {
        /* eslint-disable prefer-destructuring */
        let orderStatus = CONST_ORDERS.STATUS.find(
          (item) => item.KEY === order.status,
        );
        if (!orderStatus) {
          orderStatus = CONST_ORDERS.STATUS[0];
        }

        let paymentStatus = CONST_PAYMENT.STATUS.find(
          (item) => item.KEY === order?.PaymentRequest?.status,
        );
        if (!paymentStatus) {
          paymentStatus = CONST_PAYMENT.STATUS[0];
        }

        let paymentType = CONST_PAYMENT.TYPES.find(
          (item) => item.KEY === order?.PaymentRequest?.type,
        );
        if (!paymentType) {
          paymentType = CONST_PAYMENT.TYPES[0];
        }

        return {
          ...order,
          status: orderStatus.VALUE,
          paymentStatus: paymentStatus.VALUE,
          paymentType: paymentType.VALUE,
          PaymentRequest: {
            ...order?.PaymentRequest,
            PaymentRequestHistories: order?.PaymentRequest?.PaymentRequestHistories.map(
              (history: any) => ({
                ...history,
                message: parseJsonToValidObject(history.message),
              }),
            ),
          },
        };
      });
      if (payload?.id) {
        yield put(
          OrdersActions.getOrderSuccess({
            chargeRequestData: data[0],
          }),
        );
      } else if (isCvFull) {
        yield put(
          OrdersActions.listFullOrdersSuccess({
            chargeRequestListFull: data,
            totalCount: parseInt(response.headers.totalcount, 10),
          }),
        );
      } else {
        yield put(
          OrdersActions.listOrdersSuccess({
            chargeRequestList: data,
            totalCount: parseInt(response.headers.totalcount, 10),
          }),
        );
      }
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('orders - listOrders - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(OrdersActions.listOrdersFailure());
    yield put(DialogsActions.setClose());
  }
}

function* asyncList({
  payload,
  meta,
}: ReturnType<typeof OrdersActions.asyncListOrdersRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando pedidos' }));
    const isCvFull = payload?.isCvFull;
    delete payload?.isCvFull;

    const response = yield call(
      ordersApi[
        (payload as { isInstituition?: boolean })?.isInstituition
          ? 'list'
          : 'get'
      ],
      {
        params: { ...payload },
      },
    );

    if (response.status === 200) {
      let result = [];
      if (Array.isArray(response.data)) {
        result = response.data;
      } else {
        result = [response.data];
      }

      const data = result.map((order: any) => {
        /* eslint-disable prefer-destructuring */
        let orderStatus = CONST_ORDERS.STATUS.find(
          (item) => item.KEY === order.status,
        );
        if (!orderStatus) {
          orderStatus = CONST_ORDERS.STATUS[0];
        }

        let paymentStatus = CONST_PAYMENT.STATUS.find(
          (item) => item.KEY === order?.PaymentRequest?.status,
        );
        if (!paymentStatus) {
          paymentStatus = CONST_PAYMENT.STATUS[0];
        }

        let paymentType = CONST_PAYMENT.TYPES.find(
          (item) => item.KEY === order?.PaymentRequest?.type,
        );
        if (!paymentType) {
          paymentType = CONST_PAYMENT.TYPES[0];
        }

        return {
          ...order,
          status: orderStatus.VALUE,
          paymentStatus: paymentStatus.VALUE,
          paymentType: paymentType.VALUE,
          PaymentRequest: {
            ...order?.PaymentRequest,
            PaymentRequestHistories: order?.PaymentRequest?.PaymentRequestHistories.map(
              (history: any) => ({
                ...history,
                message: parseJsonToValidObject(history.message),
              }),
            ),
          },
        };
      });
      if (payload?.id) {
        yield put(
          OrdersActions.getOrderSuccess({
            chargeRequestData: data[0],
          }),
        );
      } else if (isCvFull) {
        yield meta.promise.resolve(response);
        yield put(
          OrdersActions.listFullOrdersSuccess({
            chargeRequestListFull: data,
            totalCount: parseInt(response.headers.totalcount, 10),
          }),
        );
      } else {
        yield put(
          OrdersActions.listOrdersSuccess({
            chargeRequestList: data,
            totalCount: parseInt(response.headers.totalcount, 10),
          }),
        );
      }
      yield meta.promise.resolve(response);
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('orders - listOrders - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(OrdersActions.listOrdersFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof OrdersActions.getOrderRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const response = yield call(ordersApi.get, { params: { ...payload } });

    if (response.status === 200) {
      yield put(
        OrdersActions.getOrderSuccess({ chargeRequestData: response.data }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('orders - getOrder - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(OrdersActions.getOrderFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
  meta,
}: ReturnType<typeof OrdersActions.createOrderRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando pedido' }));
    const response = yield call(ordersApi.create, { data: payload });

    if (response.status === 200) {
      yield put(
        OrdersActions.createOrderSuccess({ chargeRequestData: response.data }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Pedido criado com sucesso',
        }),
      );
      yield meta.promise.resolve(response);
      yield put(push(PATH.COMPANY_ORDERS.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('orders - createOrder - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(OrdersActions.createOrderFailure());
    yield meta.promise.reject(error);
    yield put(DialogsActions.setClose());
  }
}

function* repeat({
  payload,
}: ReturnType<typeof OrdersActions.repeatOrderRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando pedido' }));
    const response = yield call(ordersApi.repeat, { data: payload });

    if (response.status === 200) {
      yield put(
        OrdersActions.repeatOrderSuccess({
          chargeRequestRepeatData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Pedido criado com sucesso',
        }),
      );

      yield put(
        OrdersActions.listOrdersRequest({
          _includes: 'payment,history,order',
          _order: 'DESC',
          _sort: 'createdAt',
          _limit: 9999,
        }),
      );

      yield put(push(PATH.COMPANY_ORDERS_HISTORY.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('orders - repeatOrder - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(DialogsActions.setClose());
  }
  yield put(OrdersActions.repeatOrderFailure());
}

function* remove({
  payload,
}: ReturnType<typeof OrdersActions.deleteOrderRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo pedido' }));
    const response = yield call(ordersApi.delete, { params: { ...payload } });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Pedido excluído com sucesso',
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(OrdersActions.deleteOrderSuccess());
      yield put(
        OrdersActions.listOrdersRequest({
          _includes: 'payment',
          _order: 'DESC',
          _sort: 'createdAt',
          _limit: 9999,
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('orders - remove - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(OrdersActions.deleteOrderFailure());
    yield put(DialogsActions.setClose());
  }
}

function* getPdf({
  payload,
}: ReturnType<typeof OrdersActions.getOrderPdfRequest>): any {
  try {
    const boletoUrl = payload?.url
      ? payload?.url
      : yield call(getBoletoUrl, payload?.chargeRequestId);

    if (boletoUrl) {
      window.open(
        boletoUrl,
        '_blank', // <- This is what makes it open in a new window.
      );
    }

    //  const response = yield call(ordersApi.getPdf, { params: { ...payload } });

    // if (response.status === 200) {
    //   //   const anchor = document.getElementById("invisible-link");
    //   //   if (anchor != null) {
    //   //     const objectUrl = window.URL.createObjectURL(response.data);
    //   //     anchor.href = objectUrl;
    //   //     anchor.download = getDownloadFilename(response.headers);
    //   //     anchor.click();
    //   //     window.URL.revokeObjectURL(objectUrl);
    //   //   }
    // }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('orders - getOrder - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(OrdersActions.getOrderPdfFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
}: ReturnType<typeof OrdersActions.filterOrdersRequest>): any {
  try {
    const response = yield call(ordersApi.get, {
      params: { ...payload },
    });

    if (response.status === 200) {
      const data = response.data.map((order: any) => {
        /* eslint-disable prefer-destructuring */
        let orderStatus = CONST_ORDERS.STATUS.find(
          (item) => item.KEY === order.status,
        );
        if (!orderStatus) {
          orderStatus = CONST_ORDERS.STATUS[0];
        }

        let paymentStatus = CONST_PAYMENT.STATUS.find(
          (item) => item.KEY === order?.PaymentRequest?.status,
        );
        if (!paymentStatus) {
          paymentStatus = CONST_PAYMENT.STATUS[0];
        }

        let paymentType = CONST_PAYMENT.TYPES.find(
          (item) => item.KEY === order?.PaymentRequest?.type,
        );
        if (!paymentType) {
          paymentType = CONST_PAYMENT.TYPES[0];
        }

        return {
          ...order,
          status: orderStatus.VALUE,
          paymentStatus: paymentStatus.VALUE,
          paymentType: paymentType.VALUE,
          PaymentRequest: {
            ...order?.PaymentRequest,
            PaymentRequestHistories: order?.PaymentRequest?.PaymentRequestHistories.map(
              (history: any) => ({
                ...history,
                message: parseJsonToValidObject(history.message),
              }),
            ),
          },
        };
      });

      yield put(
        OrdersActions.filterOrdersSuccess({
          chargeRequestList: Array.isArray(data) ? data : [data],
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('orders - filterOrders - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(OrdersActions.filterOrdersFailure());
    yield put(DialogsActions.setClose());
  }
}

function* payBoleto({
  payload,
}: ReturnType<typeof OrdersActions.chargeRequestPayBoletoOrderRequest>): any {
  try {
    const formData = new FormData();
    formData.append('attachment', payload.attachment[0]);

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    yield put(DialogsActions.setOpenLoading({ title: 'Aguarde..' }));
    const response = yield call(ordersApi.payBoleto, {
      formData,
      transportOperator: transportOperator.id,
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Ordem de pagamento por boleto concluída com sucesso',
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(OrdersActions.chargeRequestPayBoletoOrderSuccess());
      yield put(CompaniesActions.listCompaniesRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('orders - Pay Boleto - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(OrdersActions.chargeRequestPayBoletoOrderFailure());
    yield put(DialogsActions.setClose());
  }
}

function* chargeRequestPay({
  payload,
}: ReturnType<typeof OrdersActions.chargeRequestPayOrderRequest>): any {
  try {
    const { body, isLastRequest, listOrdersParams } = payload;

    const formData = new FormData();
    formData.append('attachment', body.attachment[0]);

    yield put(DialogsActions.setOpenLoading({ title: 'Aguarde..' }));

    const response = yield call(ordersApi.chargeRequestPay, {
      formData,
      chargeRequestId: body.chargeRequestId,
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Ordem de pagamento por boleto concluída com sucesso',
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(OrdersActions.chargeRequestPayOrderSuccess());
      if (isLastRequest) {
        yield put(OrdersActions.listOrdersRequest(listOrdersParams));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('orders - Charge Request Pay - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(OrdersActions.chargeRequestPayOrderFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const ordersSagas = [
  takeLatest(OrdersTypes.CREATE_ORDER_REQUEST, create),
  takeLatest(OrdersTypes.CHARGE_REQUEST_REPEAT_ORDER_REQUEST, repeat),
  takeLatest(OrdersTypes.LIST_ORDERS_REQUEST, list),
  takeLatest(OrdersTypes.ASYNC_LIST_ORDERS_REQUEST, asyncList),
  takeLatest(OrdersTypes.DELETE_ORDER_REQUEST, remove),
  takeLatest(OrdersTypes.GET_ORDER_REQUEST, get),
  takeLatest(OrdersTypes.GET_ORDER_PDF_REQUEST, getPdf),
  takeLatest(OrdersTypes.FILTER_ORDER_REQUEST, filter),
  takeLatest(OrdersTypes.CHARGE_REQUEST_PAY_BOLETO_ORDER_REQUEST, payBoleto),
  takeLatest(OrdersTypes.CHARGE_REQUEST_PAY_ORDER_REQUEST, chargeRequestPay),
];
